import React from 'react';
import Icon from '../../../components/AppIcon';

const TreatmentPlanDetails = ({ treatmentPlan }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success';
      case 'in-progress': case'scheduled':
        return 'bg-primary/10 text-primary border-primary';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-6">
      {/* Treatment Overview */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-3">Treatment Overview</h3>
        <p className="text-muted-foreground leading-relaxed">
          {treatmentPlan?.description}
        </p>
      </div>
      {/* Treatment Phases */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="GitBranch" size={20} className="text-primary" />
          Treatment Phases
        </h3>
        <div className="space-y-4">
          {treatmentPlan?.phases?.map((phase, idx) => (
            <div key={phase?.id} className="bg-background rounded-lg border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sm font-semibold text-primary">{idx + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{phase?.phaseName}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{phase?.description}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {phase?.estimatedStartDate} to {phase?.estimatedEndDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="DollarSign" size={14} />
                        ${phase?.cost?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(phase?.status)}`}>
                  {phase?.status}
                </span>
              </div>

              {/* Procedures */}
              {phase?.procedures && phase?.procedures?.length > 0 && (
                <div className="mt-3 pl-11">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Procedures:</p>
                  <div className="space-y-2">
                    {phase?.procedures?.map(proc => (
                      <div key={proc?.id} className="flex items-center justify-between text-sm bg-card rounded p-2">
                        <div className="flex-1">
                          <span className="font-medium text-foreground">{proc?.procedureCode}</span>
                          <span className="text-muted-foreground ml-2">{proc?.procedureName}</span>
                          {proc?.toothNumber && (
                            <span className="text-primary ml-2">({proc?.toothNumber})</span>
                          )}
                        </div>
                        <span className="text-foreground font-medium">${proc?.cost?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Educational Resources */}
      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Understanding Your Treatment</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Each phase builds upon the previous one to ensure optimal results</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Treatment timeline may be adjusted based on your healing progress</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>All procedures follow evidence-based clinical protocols</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                <span>Your provider will monitor progress at each stage</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanDetails;